import { useQuery } from '@tanstack/react-query';
import { costTablesListQuery } from './costTableQueries';
import { allGroupSetsQuery } from './groupSetQueries';
import { selectCurrentTree } from '@Selectors/Rehab.selectors';
import type { CostTableSummary } from '@Types/cost.types';
import { getNewAssetIds } from '@Utils/assetSchemaUtils';

export function useFilteredCostTables(enabled = true): CostTableSummary[] {
	const { data: costTables = [] } = useQuery(costTablesListQuery(enabled));
	const { data: allTrees = [] } = useQuery({
		...allGroupSetsQuery(),
		enabled,
	});
	const currentTree = selectCurrentTree();

	if (!currentTree?.systemType || !currentTree?.assetType) {
		return [];
	}

	// Build a set of tree IDs whose systemType/assetType match the current tree.
	// allTrees comes from the raw API so systemType/assetType are plain strings,
	// and rows may hold either a legacy spelling ('Sanitary Sewer', 'pipe') or a
	// schema id ('SanitarySewer', 'wwPipe') depending on when they were written.
	// Normalise that side through getNewAssetIds and compare schema ids, so a tree
	// matches its cost tables regardless of which spelling its row uses.
	//
	// Only that side. currentTree is already normalised - both its writers run it
	// through getNewAssetIds (Rehab.sagas.ts for a loaded tree, NewRehab for a new
	// one) - so it is compared raw. That invariant lives in other files, and this
	// comparison breaks silently if it ever stops holding.
	const matchingTreeIds = new Set(
		allTrees
			.filter(t => {
				const { systemTypeId, assetTypeId } = getNewAssetIds({
					systemType: t.systemType,
					assetType: t.assetType,
				});
				return (
					systemTypeId.id === currentTree.systemType.id &&
					assetTypeId.id === currentTree.assetType.id
				);
			})
			.map(t => t._id),
	);

	return costTables.filter(
		ct =>
			ct.asset_group_set_id && matchingTreeIds.has(ct.asset_group_set_id),
	);
}
